import Vue from "vue";
import { holderTypeWithOwner, ObjectModelRecordHolderType } from "~/enums/objectModelRecordHolderType";
import { ObjectHelper } from "~/helpers/objectHelper";
import { IObjectModelEntry } from "~/interfaces/objectModelInterfaces";
import Correspondence from "~/models/formSubmission/correspondence";
import Note from "~/models/formSubmission/correspondences/note";
import { RelationType } from "~/models/relationType";
import IRepo from "../../interfaces/repo";
import ObjectModelEntryProvider from "../../providers/objectModelEntryProvider";
import Repo from "../../state/modules/repo";
import ObjectModel from "../objectModel";
import EntryRelation from "../objectModelRelations/entryRelation";
import AccountRelation from "../objectModelRelations/accountRelation";
import ObjectModelRelation from "../objectModelRelations/objectModelRelation";
import ValidationError from "../validationError";
import BaseObjectModelEntry from "./baseObjectModelEntry";
import ObjectModelEntryValidator from "./validators/objectModelEntryValidator";
import {IContentViewData} from "~/models/objectModelEntries/contentViewData";

export default class ObjectModelEntry extends BaseObjectModelEntry implements IObjectModelEntry {
  static repo: IRepo<ObjectModelEntry, ObjectModelEntryProvider> = new Repo<ObjectModelEntry, ObjectModelEntryProvider>('objectModelEntries', ObjectModelEntry, {}, new ObjectModelEntryProvider(ObjectModelEntry, 'object_model_entry', '/object_models/:object_model_id/entries', false));
  object_model_id: string;
  published_object_model: ObjectModel;
  owner_email: string;
  entity_owner: { name: string, phone: string, address: string };
  owner: { id: string, email: string, name: string };
  creator: { id: string, name: string };
  owner_name: string;
  object_model_holder_name: string;
  creator_name: string;
  list_view_id: number;
  entry_relations:  { [key: string]: EntryRelation} = {} ;
  inverse_entry_relations:{ [key: string]: EntryRelation} = {} ;
  account_relations: { [key: string]: AccountRelation } = {};
  file_uploads: any[];
  claims: Array<any>
  is_shell: boolean;
  correspondences: Correspondence[];
  notes: Note[] = [];
  relation_identifier: string;

  // holds read-only data from server necessary for rendering a specific view
  view_data?: IContentViewData;

  except = [
    'published_object_model',
    'validator',
    'createEmbeddedObject',
    'entry_relations',
    'inverse_entry_relations',
    'account_relations',
    'view_data',
  ];

  validator: ObjectModelEntryValidator;

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    const models = {
      'entry_relations': {type:EntryRelation , relationType: RelationType.dictionary},
      'inverse_entry_relations': {type: EntryRelation, relationType: RelationType.dictionary},
      'account_relations': {type: AccountRelation, relationType: RelationType.dictionary},
    };
    return models;
  }

  // back end still uses a field, but no need on front end
  // since moving owner type determination to object model relationship
  set has_owner(val:boolean) {}
  get has_owner(): boolean {
    return holderTypeWithOwner.includes(this.owner_type)
  }

  createEmbeddedObject = {
    entry_relations: (parent, json): EntryRelation => new EntryRelation(parent,'entry_relations', json),
    inverse_entry_relations: (parent, json): EntryRelation => new EntryRelation(parent, 'inverse_entry_relations', json),
    account_relations: (parent, json): AccountRelation => new AccountRelation(parent, 'account_relations', json),
  };
  get entryRelationsArray(): EntryRelation[]{
    return ObjectHelper.toArray(this.entry_relations)
  }
  get inverseEntryRelationsArray(): EntryRelation[]{
    return ObjectHelper.toArray(this.inverse_entry_relations)
  }
  // user_relations would be a much better name for this. Consider updating during a cooldown sprint
  get accountRelationsArray(): AccountRelation[] {
    return ObjectHelper.toArray(this.account_relations);
  }


  appendEntryRelation(json = {}){
    let entryRelation = this.createEmbeddedObject.entry_relations(this, json );
    Vue.set(this['entry_relations'], entryRelation.client_id, entryRelation);
    return entryRelation
  }

  get owner_type(): ObjectModelRecordHolderType{
    return this.published_object_model.holder_type
  }

  appendInverseEntryRelation(json = {}){
    let entryRelation = this.createEmbeddedObject.inverse_entry_relations(this, json );
    Vue.set(this['inverse_entry_relations'], entryRelation.client_id, entryRelation);
    return entryRelation
  }

  appendAccountRelation(json={}) {
    const accountRelation = this.createEmbeddedObject.account_relations(this, json);
    Vue.set(this['account_relations'], accountRelation.client_id, accountRelation);
    return accountRelation;
  }


  static get provider(): ObjectModelEntryProvider {
    return this.repo.provider;
  }

  static get displayName(): string {
    return 'Entry';
  }

  get inverseEntryRelations() {
    return this.inverse_entry_relations || []
  }

  get relations(): ObjectModelRelation[] {
    return Object.keys(this.published_object_model.object_model_relations).map(key => this.published_object_model.object_model_relations[key])
  }

  get inverseRelations(): ObjectModelRelation[] {
    return this.published_object_model.inverse_object_model_relations.all
  }

  get allRelations(): ObjectModelRelation[] {
    return this.relations.concat(this.inverseRelations)
  }

  validate() {
    if (!this.validator) {
      Vue.set(this, 'validator', new ObjectModelEntryValidator(this));
    }
    this.validator.validate();
  }

  get validationErrors(): ValidationError[] {
    if (this.validator) {
      return this.validator.errors;
    }
    return [];
  }
}